import urls from "global/constants/UrlConstants";
import { getCallParams, getNoAuthCallParams, makeCall } from "utils/service";

// UI shape for blog list item
export interface BlogListItem {
  id: number | string;
  title: string;
  image: string;
  author: string;
  publishDate: string;
  topic: string;
  tags: string[];
  description: string;
  isLiked: boolean;
  likes: number;
}

// UI shape for blog detail
export interface BlogDetailItem extends BlogListItem {
  subtitle?: string;
  authorLink?: string;
  quote?: string;
  content?: string;
  metaDescription?: string;
  seoTitle?: string;
}

// Normalize raw API item to list shape (matches getBlogById / getAllBlogs response)
function mapRawToListItem(raw: any): BlogListItem {
  const id = raw?.id ?? raw?.blog_id ?? "";
  const title = raw?.title ?? "";
  const image = raw?.image ?? "";
  const author = raw?.author ?? "";
  const publishDate = raw?.date ?? raw?.publish_date ?? raw?.created_at ?? "";
  const topic = raw?.key ?? raw?.topic ?? raw?.category ?? "";
  let tags: string[] = [];
  if (Array.isArray(raw?.tags)) tags = raw.tags;
  else if (typeof raw?.tags === "string")
    tags = raw.tags
      .split(",")
      .map((t: string) => t.trim())
      .filter(Boolean);
  else if (raw?.tag_list)
    tags = Array.isArray(raw.tag_list) ? raw.tag_list : [raw.tag_list];
  const description = raw?.summary ?? raw?.description ?? raw?.excerpt ?? "";
  return {
    id,
    title: String(title),
    image: String(image),
    author: String(author),
    publishDate: String(publishDate),
    topic: String(topic),
    tags,
    description: String(description),
    isLiked: raw?.isLiked ?? false,
    likes: raw?.likes ?? 0,
  };
}

// Normalize raw API item to detail shape (getBlogById: blog_id, id, key, title, headline, summary, image, date, author, content, ...)
function mapRawToDetailItem(raw: any): BlogDetailItem {
  const list = mapRawToListItem(raw);
  const subtitle = raw?.headline ?? raw?.subtitle ?? "";
  const authorLink = raw?.url ?? raw?.author_link ?? raw?.parenturl ?? "#";
  const quote = raw?.quote ?? "";
  const content = raw?.content ?? raw?.body ?? raw?.html_content ?? "";
  const metaDescription = raw?.meta_description ?? "";
  const seoTitle = raw?.seo_title ?? raw?.title ?? "";
  return {
    ...list,
    subtitle: String(subtitle),
    authorLink: String(authorLink || "#"),
    quote: String(quote),
    content: String(content),
    metaDescription: String(metaDescription),
    seoTitle: String(seoTitle),
  };
}

// Extract single blog from getBlogById response (root object or response.blog / response.data)
function extractBlogDetail(response: any): any | null {
  if (response == null) return null;
  const raw = response.blog ?? response.data ?? response;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  if (raw.blog_id != null || raw.id != null || raw.title != null) return raw;
  return null;
}

// Extract list from getAllBlogs response
function extractBlogList(response: any): any[] {
  if (!response) return [];
  const list = response.blogs ?? response.data ?? response.result ?? response;
  return Array.isArray(list) ? list : [];
}

export const getBlogById = async (
  blogId: string,
): Promise<BlogDetailItem | null> => {
  try {
    const callParams = getNoAuthCallParams("GET");
    const response: any = await makeCall(urls.getBlogById + blogId, callParams);
    const raw = extractBlogDetail(response);
    return raw ? mapRawToDetailItem(raw) : null;
  } catch (error: any) {
    throw error;
  }
};

export const getAllBlogs = async (): Promise<BlogListItem[]> => {
  try {
    const callParams = getNoAuthCallParams("GET");
    const response: any = await makeCall(urls.getAllBlogs, callParams);
    const list = extractBlogList(response);
    return list.map((item: any) => mapRawToListItem(item));
  } catch (error: any) {
    throw error;
  }
};

export const getBlogComments = async (blogId: string): Promise<any> => {
  const url = urls.blogComments + blogId + "/comments";
  try {
    const callParams = getNoAuthCallParams("GET");
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const addBlogComment = async (
  blogId: string,
  comment: any,
): Promise<any> => {
  const url = urls.blogComments + blogId + "/comments";
  try {
    const callParams = getNoAuthCallParams("POST", comment);
    const response: any = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const likeDislikeBlog = async (
  blogId: string,
  isLike: boolean,
): Promise<any> => {
  try {
    const callParams = await getCallParams("POST", { blogId, isLike });
    const response: any = await makeCall(urls.likeDislikeBlog, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};
